import { addMonths } from 'date-fns'
import { ChevronRight, ExternalLink, FileText, Info } from 'lucide-react'
import { useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import {
  formatInstantDate,
  formatRefillInterval,
  getPrimaryOrder,
  isNoteEmpty,
  requireClinic,
} from '@snootsvet/shared'

import { FormattedDate, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Pagination } from '@/frontend/components/pagination'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from '@/frontend/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { OrderStatusBadge } from '@/frontend/features/orders/order-badges'
import { PrescriptionActionsMenu } from '@/frontend/features/patient/prescription-actions'
import {
  RefillBadge,
  RefillIntervalBadge,
  RefillStatusBadge,
} from '@/frontend/features/patient/refill-badges'
import { NoteContent } from '@/frontend/features/patient/timeline/note-entry'
import { useGetConsultation } from '@/frontend/hooks/api/consultations/use-get-consultation'
import { useListPatientTreatments } from '@/frontend/hooks/api/patients/use-list-patient-treatments'
import {
  groupPrescriptions,
  type Prescription,
  type PrescriptionGroup,
} from '@/frontend/lib/treatments'
import { routes } from '@/frontend/routes'

function PrescriptionSourceLink({ rx }: { rx: Prescription }) {
  const href = `${generatePath(routes.patient, { patientId: rx.patientId })}#${rx.consultationId ?? rx.id}`

  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 hover:text-primary"
    >
      <FormattedDate value={rx.createdAt} clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null} />
      <ExternalLink className="size-3 shrink-0 -translate-y-px" aria-hidden="true" />
      <span className="sr-only"> (opens in a new tab)</span>
    </Link>
  )
}

function ConsultationNotes({ id }: { id: string }) {
  const { data, isPending, isError, refetch } = useGetConsultation(id)
  if (isPending) return <p>Loading notes...</p>
  if (isError)
    return (
      <div role="alert">
        <p>Unable to load consultation notes.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  const notes = data.notes.filter((note) => !isNoteEmpty(note))
  return notes.length ? (
    <div className="space-y-3">
      {notes.map((note) => (
        <NoteContent key={note.id} note={note} />
      ))}
    </div>
  ) : (
    <p>No consultation notes recorded.</p>
  )
}

function PrescriptionNotesPreview({ rx }: { rx: Prescription }) {
  const [open, setOpen] = useState(false)
  if (!rx.consultationId && !rx.clinicalNotes?.trim() && !rx.internalNotes?.trim()) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Preview notes for ${rx.productInfo.name}`}
            title="Preview notes"
          />
        }
      >
        <FileText className="size-4" strokeWidth={1} />
      </PopoverTrigger>
      <PopoverContent className="max-h-[min(40rem,80vh)] w-[32rem] max-w-[calc(100vw-2rem)] overflow-y-auto p-4 text-left text-sm">
        <PopoverTitle>
          {rx.consultationId ? 'Consultation' : 'Standalone Prescription'}
        </PopoverTitle>
        {open &&
          (rx.consultationId ? (
            <ConsultationNotes id={rx.consultationId} />
          ) : (
            <div className="space-y-3">
              {rx.clinicalNotes && (
                <div>
                  <p className="font-medium">Clinical notes</p>
                  <p className="whitespace-pre-wrap">{rx.clinicalNotes}</p>
                </div>
              )}
              {rx.internalNotes && (
                <div>
                  <p className="font-medium">Internal notes</p>
                  <p className="whitespace-pre-wrap">{rx.internalNotes}</p>
                </div>
              )}
            </div>
          ))}
      </PopoverContent>
    </Popover>
  )
}

export function PrescriptionsDialog({
  patientId,
  open,
  onOpenChange,
}: {
  patientId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data, isPending, isError, refetch } = useListPatientTreatments(patientId)
  const groups = groupPrescriptions(data ?? [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100%-4rem)] flex-col overflow-hidden p-0 sm:max-w-7xl">
        <DialogHeader className="shrink-0 px-4 pt-4 pr-12">
          <DialogTitle>Prescriptions</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable] px-4 pb-4 space-y-6">
          <DialogDescription className="text-sm">
            <p>
              Active prescriptions include internal Snoots prescriptions with authorised refills and
              external prescriptions that are still within their expiry date. One-off prescriptions,
              expired prescriptions and cancelled prescriptions are shown in History.
            </p>
            <p className="mt-1">
              Use the notes icon to preview consultation notes or any clinical and internal notes
              attached to a standalone prescription. Click a linked prescription date to open the
              original patient record entry in a new tab.
            </p>
          </DialogDescription>
          {isPending ? (
            <p className="text-muted-foreground">Loading prescriptions...</p>
          ) : isError ? (
            <div role="alert">
              <p>Unable to load prescriptions.</p>
              <Button variant="outline" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          ) : (
            <>
              <PrescriptionList
                title="Active"
                patientId={patientId}
                groups={groups.filter((group) => group.latest.isActivePrescription)}
              />
              <PrescriptionList
                title="History"
                patientId={patientId}
                groups={groups.filter((group) => !group.latest.isActivePrescription)}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PrescribedDate({ rx }: { rx: Prescription }) {
  return (
    <FormattedDate value={rx.createdAt} clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null} />
  )
}

function PrescriptionExpiry({ rx }: { rx: Prescription }) {
  const format = useViewerFormat()
  if (rx.cancelledAt)
    return (
      <span className="inline-flex items-center gap-1">
        <FormattedDate
          value={rx.cancelledAt}
          clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null}
        />
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus exposes the cancellation reason tooltip.
                tabIndex={0}
                role="img"
                className="inline-flex text-destructive-accent"
                aria-label="Cancellation reason"
              />
            }
          >
            <Info className="size-3.5" aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>
            {rx.cancelledReason || 'No cancellation reason recorded.'}
          </TooltipContent>
        </Tooltip>
      </span>
    )
  if (!rx.isExternal && (rx.repeatsAllowed ?? 0) === 0) return <>-</>
  if (rx.validUntil) return <>{formatInstantDate(rx.validUntil, format)}</>
  return (
    <FormattedDate
      value={addMonths(new Date(rx.createdAt), 6).toISOString()}
      clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null}
    />
  )
}

function ProductRows({ group, patientId }: { group: PrescriptionGroup; patientId: string }) {
  const [expanded, setExpanded] = useState(false)
  const latest = group.latest
  const prescriptions = [latest, ...group.previous]
  const first = prescriptions[prescriptions.length - 1]

  return (
    <>
      <TableRow className="text-sm">
        <TableCell className="whitespace-normal">
          <button
            type="button"
            className="flex w-full items-center gap-2 text-left hover:text-primary"
            aria-expanded={expanded}
            aria-controls={`prescription-history-${latest.id}`}
            aria-label={`${expanded ? 'Hide' : 'Show'} prescription history for ${latest.productInfo.name}`}
            onClick={() => setExpanded(!expanded)}
          >
            <ChevronRight
              aria-hidden="true"
              className={`size-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
            <span>
              <span className="font-medium">{latest.productInfo.name}</span>
              <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {prescriptions.length}{' '}
                  {prescriptions.length === 1 ? 'prescription' : 'prescriptions'}
                </span>
                {latest.isExternal && (
                  <Badge variant="pending" size="xs">
                    External
                  </Badge>
                )}
              </span>
            </span>
          </button>
        </TableCell>
        <TableCell className="text-center">
          <PrescribedDate rx={first} />
        </TableCell>
        <TableCell className="text-center">
          <PrescriptionSourceLink rx={latest} />
        </TableCell>
        <TableCell className="text-center whitespace-normal">
          {latest.productInfo.quantityLabel}
        </TableCell>
        <TableCell className="text-center whitespace-normal">
          {latest.staff.firstName} {latest.staff.lastName}
        </TableCell>
        <TableCell className="text-center">
          <PrescriptionExpiry rx={latest} />
        </TableCell>
        <TableCell className="text-center whitespace-normal">
          <div className="flex flex-col items-center gap-1">
            {!latest.isExternal && (latest.repeatsAllowed ?? 0) > 0 ? (
              <RefillBadge rx={latest} />
            ) : (
              '-'
            )}
            <RefillIntervalBadge rx={latest} />
            <RefillStatusBadge rx={latest} />
          </div>
        </TableCell>
        <TableCell className="text-center">
          <PrescriptionNotesPreview rx={latest} />
        </TableCell>
        <TableCell>
          <div className="flex justify-center">
            <PrescriptionActionsMenu rx={latest} patientId={patientId} nested />
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow id={`prescription-history-${latest.id}`} className="bg-muted/20">
          <TableCell colSpan={9} className="whitespace-normal px-0 py-4">
            <section
              className="grid grid-cols-[4rem_calc(33%-4rem)_11%_10%_15%_10%_12%_5%_4%]"
              aria-label={`Prescription history for ${latest.productInfo.name}`}
            >
              <div className="pb-2 text-center text-xs font-medium text-muted-foreground">
                {!latest.isExternal && 'Orders'}
              </div>
              {[
                'Instructions',
                'Prescribed',
                'Quantity',
                'Prescribed by',
                'Expiry',
                'Refills authorised',
                'Notes',
              ].map((heading) => (
                <div
                  key={heading}
                  className={`px-3 pb-2 text-xs font-medium text-muted-foreground ${heading === 'Instructions' ? 'text-left' : 'text-center'}`}
                >
                  {heading}
                </div>
              ))}
              <div />
              {prescriptions.map((rx) => (
                <PrescriptionHistoryEntry key={rx.id} rx={rx} />
              ))}
            </section>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function PrescriptionHistoryEntry({ rx }: { rx: Prescription }) {
  const [ordersExpanded, setOrdersExpanded] = useState(false)
  const hasOrders = !rx.isExternal && rx.orders.length > 0
  return (
    <article className="col-span-full grid grid-cols-subgrid items-center gap-y-2 border-b py-3 text-center text-sm last:border-0">
      <div className="px-3">
        {hasOrders ? (
          <button
            type="button"
            className="flex h-8 items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title={`${rx.orders.length} ${rx.orders.length === 1 ? 'order' : 'orders'}`}
            aria-label={`${ordersExpanded ? 'Hide' : 'Show'} ${rx.orders.length} orders`}
            aria-expanded={ordersExpanded}
            aria-controls={`prescription-orders-${rx.id}`}
            onClick={() => setOrdersExpanded(!ordersExpanded)}
          >
            <ChevronRight
              aria-hidden="true"
              className={`size-4 transition-transform ${ordersExpanded ? 'rotate-90' : ''}`}
            />
            <span className="tabular-nums">{rx.orders.length}</span>
          </button>
        ) : !rx.isExternal ? (
          <span className="text-muted-foreground">–</span>
        ) : null}
      </div>
      <p className="px-3 whitespace-pre-wrap text-left">
        {rx.instructions || 'No instructions recorded.'}
      </p>
      <div className="px-3">
        <PrescriptionSourceLink rx={rx} />
      </div>
      <div className="px-3">{rx.productInfo.quantityLabel}</div>
      <div className="px-3">
        {rx.staff.firstName} {rx.staff.lastName}
      </div>
      <div className="px-3">
        <PrescriptionExpiry rx={rx} />
      </div>
      <div className="px-3">
        {rx.repeatsAllowed ?? 0}
        {rx.refillInterval && (
          <div className="text-xs text-muted-foreground">
            Every {formatRefillInterval(rx.refillInterval)}
          </div>
        )}
      </div>
      <div>
        <PrescriptionNotesPreview rx={rx} />
      </div>
      <div />
      {hasOrders && ordersExpanded && (
        <div
          id={`prescription-orders-${rx.id}`}
          className="relative col-span-full mt-3 before:pointer-events-none before:absolute before:inset-y-0 before:left-5 before:border-l before:border-border"
        >
          <Table
            variant="borderless"
            size="sm"
            className="table-fixed [&_th]:text-center [&_th]:text-xs [&_th]:font-medium [&_th]:text-muted-foreground [&_td]:text-center"
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-[13%]">Status</TableHead>
                <TableHead className="w-[12%]">Clinic</TableHead>
                <TableHead className="w-[10%]">
                  {(rx.repeatsAllowed ?? 0) > 0 ? 'Instalment' : null}
                </TableHead>
                <TableHead className="w-[11%]">Ordered At</TableHead>
                <TableHead className="w-[10%]">Quantity</TableHead>
                <TableHead className="w-[10%]">Dispensed</TableHead>
                <TableHead className="w-[34%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...rx.orders]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((order) => (
                  <TableRow
                    key={order.id}
                    className="relative border-0 text-xs after:pointer-events-none after:absolute after:inset-x-5 after:bottom-0 after:border-b after:border-border last:after:hidden"
                  >
                    <TableCell>
                      <OrderStatusBadge status={order.status} size="xs" />
                    </TableCell>
                    <TableCell>{requireClinic(order.clinicId).name}</TableCell>
                    <TableCell>
                      {(rx.repeatsAllowed ?? 0) > 0
                        ? order.isRefill
                          ? 'Refill'
                          : 'Initial'
                        : null}
                    </TableCell>
                    <TableCell>
                      <FormattedDate value={order.createdAt} clinicId={order.clinicId} />
                    </TableCell>
                    <TableCell>
                      {order.quantity} {rx.product?.unit}
                    </TableCell>
                    <TableCell>
                      {order.dispensedAt ? (
                        <FormattedDate value={order.dispensedAt} clinicId={order.clinicId} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      )}
    </article>
  )
}

function PrescriptionList({
  title,
  patientId,
  groups,
}: {
  title: 'Active' | 'History'
  patientId: string
  groups: PrescriptionGroup[]
}) {
  const [requestedPage, setPage] = useState(1)
  const paginated = title === 'History'
  const totalPages = Math.max(1, Math.ceil(groups.length / 10))
  const page = Math.min(requestedPage, totalPages)
  const visible = paginated ? groups.slice((page - 1) * 10, page * 10) : groups

  return (
    <section aria-label={title} className="space-y-3">
      <h2 className="text-headline-md">
        {title} <span className="text-muted-foreground">({groups.length})</span>
      </h2>
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No prescriptions.</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[1050px]">
            <Table variant="borderless" className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[22%] text-sm">
                    <span className="pl-6">Medication</span>
                  </TableHead>
                  <TableHead className="w-[11%] text-center text-sm whitespace-normal">
                    First prescribed
                  </TableHead>
                  <TableHead className="w-[11%] text-center text-sm whitespace-normal">
                    Last prescribed
                  </TableHead>
                  <TableHead className="w-[10%] text-center text-sm whitespace-normal">
                    Latest quantity
                  </TableHead>
                  <TableHead className="w-[15%] text-center text-sm whitespace-normal">
                    Last prescribed by
                  </TableHead>
                  <TableHead className="w-[10%] text-center text-sm">Expiry</TableHead>
                  <TableHead className="w-[12%] text-center text-sm whitespace-normal">
                    Refills
                  </TableHead>
                  <TableHead className="w-[5%] text-center text-sm">Notes</TableHead>
                  <TableHead className="w-[4%] text-center text-sm">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((group) => (
                  <ProductRows key={group.latest.id} group={group} patientId={patientId} />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {paginated && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
    </section>
  )
}
